import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useAxios from '../../../hooks/useAxios';
import usePrevious from '../../../hooks/usePrevious';
import { loginUser } from '../../../redux/actions/userActions';
import { LoginResponse } from '../../../types/api/users';
import { ReduxState } from '../../../types/redux';

// The app's own root-path `<Redirect to="/welcome">` (see Routes.tsx) fires its
//  history.replace() from componentDidMount, which runs at layout-effect timing -
//  before ANY useEffect in the tree, regardless of component order. That redirect
//  drops the URL hash (a bare path replace carries no hash forward), so by the time
//  a useEffect here could read window.location.hash, it's already gone. Capturing it
//  at module-evaluation time (synchronous, runs before React renders anything at all)
//  is the only way to reliably see the hash the new customer app handed off.
let pendingRefreshTokenFromInitialLoad = new URLSearchParams(
  window.location.hash.replace(/^#/, '')
).get('refresh_token');

// Picks up a `refresh_token` left in the URL hash by the new customer app
//  and uses it to log the user in here, without asking for credentials again.
function SsoHandoffNotifier(): null {
  const dispatch = useDispatch();

  const isLoggedIn = useSelector<ReduxState, ReduxState['user']['isLoggedIn']>(
    (state) => state.user.isLoggedIn
  );

  const { data, error, reload } = useAxios<LoginResponse>(
    { url: '/users/login', method: 'POST' },
    { skipWhen: true }
  );

  const prevData = usePrevious(data);
  const hasAttempted = useRef(false);

  const handleIncomingRefreshToken = useCallback(() => {
    const refreshToken =
      pendingRefreshTokenFromInitialLoad ??
      new URLSearchParams(window.location.hash.replace(/^#/, '')).get(
        'refresh_token'
      );

    pendingRefreshTokenFromInitialLoad = null;

    if (isLoggedIn || hasAttempted.current) {
      return;
    }

    if (!refreshToken) {
      return;
    }

    hasAttempted.current = true;

    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );

    reload({
      data: {
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
        client_id: process.env.REACT_APP_CLIENT_ID,
        client_secret: process.env.REACT_APP_CLIENT_SECRET,
      },
    });
  }, [isLoggedIn, reload]);

  // A hash-only URL change (e.g. arriving via a link that reuses an already-open
  //  tab/document) doesn't reload the page, so mount alone isn't enough here -
  //  we also need to react to `hashchange` explicitly.
  useEffect(() => {
    handleIncomingRefreshToken();

    window.addEventListener('hashchange', handleIncomingRefreshToken);

    return () => {
      window.removeEventListener('hashchange', handleIncomingRefreshToken);
    };
  }, [handleIncomingRefreshToken]);

  useEffect(() => {
    if (!data || data === prevData) {
      return;
    }

    dispatch(loginUser(data, true, Date.now()));
  }, [data, dispatch, prevData]);

  useEffect(() => {
    if (!error) {
      return;
    }

    Sentry.captureException(error, {
      extra: { context: 'SsoHandoffNotifier refresh_token exchange failed' },
    });
  }, [error]);

  return null;
}

export default SsoHandoffNotifier;
